import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import moment from 'moment';
import {
  getConvocacaoContagem,
  getNomeArquivoNotificacao,
  getRelatorioJobFromNotificacao,
  isNotificacaoLida,
  isRelatorioDisponivel,
  isRelatorioGerando,
  moduloLabel,
} from '../../../helpers/notificacao';

const MODULO_ICON = {
  PROCEDIMENTO: ArticleOutlinedIcon,
  CONVOCACAO: CampaignOutlinedIcon,
  HOLERITE: ReceiptLongOutlinedIcon,
  DOCUMENTO: BadgeOutlinedIcon,
  RELATORIO: DescriptionOutlinedIcon,
};

const MODULO_COLOR = {
  PROCEDIMENTO: 'primary',
  CONVOCACAO: 'info',
  HOLERITE: 'success',
  DOCUMENTO: 'warning',
  RELATORIO: 'primary',
};

const formatWhen = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.fromNow() : '';
};

const formatDateTime = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '';
};

const countChipSx = (tone) => ({
  fontSize: 11.5,
  fontWeight: 700,
  borderRadius: 999,
  px: 1,
  py: 0.25,
  color: tone,
  border: 1,
  borderColor: 'divider',
});

const getFileCardSx = (beside) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: beside ? { xs: '100%', sm: 'auto' } : '100%',
  maxWidth: beside ? { sm: 280 } : 360,
  minWidth: beside ? { sm: 200 } : undefined,
  flexShrink: 0,
  mt: beside ? 0 : 1,
  px: 1.25,
  py: 0.75,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'action.hover',
  alignSelf: beside ? { sm: 'center' } : 'stretch',
});

const NotificacaoItem = ({
  item,
  showMessage,
  onDownload,
  downloading,
  fileBeside,
}) => {
  const Icon = MODULO_ICON[item.modulo] || NotificationsOutlinedIcon;
  const color = MODULO_COLOR[item.modulo] || 'primary';
  const unread = !isNotificacaoLida(item);
  const contagem = getConvocacaoContagem(item);
  const gerando = isRelatorioGerando(item);
  const job = isRelatorioDisponivel(item) ? getRelatorioJobFromNotificacao(item) : null;
  const nomeArquivo = getNomeArquivoNotificacao(item);
  const entregueEm = item.entregueEm || item.criadoEm;
  const visualizadaEm = item.visualizadaEm || item.dataLeitura;
  const entregueLabel = formatDateTime(entregueEm);
  const visualizadaLabel = formatDateTime(visualizadaEm);
  const modulo = moduloLabel(item.modulo);
  const hasFileCard = gerando || (job && onDownload);
  const fileCardSx = getFileCardSx(fileBeside);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: (theme) => theme.palette[color].main,
          color: (theme) => theme.palette[color].contrastText,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: fileBeside
            ? { xs: 'column', sm: 'row' }
            : 'column',
          alignItems: fileBeside
            ? { xs: 'stretch', sm: 'center' }
            : 'stretch',
          justifyContent: 'flex-start',
          flexWrap: fileBeside ? { sm: 'wrap' } : 'nowrap',
          gap: fileBeside ? { xs: 1, sm: 1.5 } : 0,
        }}
      >
        <Box
          sx={{
            flex: fileBeside ? '0 1 auto' : 1,
            minWidth: 0,
            maxWidth: fileBeside
              ? { xs: '100%', sm: 'calc(100% - 300px)' }
              : '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {unread && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: unread ? 700 : 500 }}
            >
              {item.titulo}
            </Typography>
          </Box>
          {showMessage && item.mensagem && !contagem && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.mensagem}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
          >
            {[
              modulo,
              entregueEm ? formatWhen(entregueEm) : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
          {(entregueLabel || visualizadaLabel) && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
            >
              {[
                entregueLabel ? `Entregue ${entregueLabel}` : '',
                visualizadaLabel ? `Visualizada ${visualizadaLabel}` : '',
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {contagem && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
              <Box
                component="span"
                sx={countChipSx('success.main')}
              >
                {`${contagem.aceitas} aceitaram`}
              </Box>
              <Box
                component="span"
                sx={countChipSx('error.main')}
              >
                {`${contagem.negadas} negaram`}
              </Box>
              <Box
                component="span"
                sx={countChipSx('warning.main')}
              >
                {`${contagem.expiradas} não responderam`}
              </Box>
            </Box>
          )}
        </Box>

        {hasFileCard && gerando && (
          <Box
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={fileCardSx}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: 18, color: 'warning.main' }}
            />
            <Typography
              variant="caption"
              noWrap
              sx={{ flex: 1, fontWeight: 600 }}
            >
              {nomeArquivo || 'Gerando documento…'}
            </Typography>
            <CircularProgress
              size={16}
              sx={{ color: 'warning.main', flexShrink: 0 }}
            />
          </Box>
        )}

        {hasFileCard && job && onDownload && (
          <Box
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={fileCardSx}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: 18, color: 'info.main' }}
            />
            <Typography
              variant="caption"
              noWrap
              sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
            >
              {nomeArquivo || 'Documento Word'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={downloading}
              onClick={(event) => {
                event.stopPropagation();
                onDownload(job);
              }}
              startIcon={downloading ? (
                <CircularProgress
                  size={14}
                  color="inherit"
                />
              ) : undefined}
              sx={{ flexShrink: 0 }}
            >
              {downloading ? 'Baixando...' : 'Baixar'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

NotificacaoItem.propTypes = {
  item: PropTypes.shape({
    modulo: PropTypes.string,
    lida: PropTypes.bool,
    titulo: PropTypes.string,
    mensagem: PropTypes.string,
    criadoEm: PropTypes.string,
    entregueEm: PropTypes.string,
    visualizadaEm: PropTypes.string,
    dataLeitura: PropTypes.string,
    evento: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  showMessage: PropTypes.bool,
  onDownload: PropTypes.func,
  downloading: PropTypes.bool,
  fileBeside: PropTypes.bool,
};

NotificacaoItem.defaultProps = {
  showMessage: false,
  onDownload: null,
  downloading: false,
  fileBeside: false,
};

export default NotificacaoItem;
